import { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  User,
  FolderOpen,
  FileText,
  Settings,
  LogOut,
  MoreVertical,
  SearchCheck,
  Star,
} from 'lucide-react';
import { logoutThunk } from '../../redux/slices/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import Logo from '../../assets/Logo.svg';
import CollapsedLogo from '../../assets/collapsed.svg';
import CollapsedLogoDark from '../../assets/dark2.svg';
import { toast } from 'react-hot-toast';
import LogoDark from '../../assets/darkest.svg';
import PlanUsageCard from '../usage/PlanUsageCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from 'react-tooltip';
import ScaleLogo from '../../assets/scale-balance-white.svg';
import ScaleLogo2 from '../../assets/scale-balance-black.svg';
import { clearReport } from '../../redux/slices/reportSlice';
import { clearDocument } from '../../redux/slices/documentSlice';
import {
  fetchChatSessions,
  renameChatSessionThunk,
  updateChatTitleLocally,
} from '../../redux/slices/chatSessionsSlice';
import { resetStreaming } from '../../redux/slices/streamingSlice';
import { deleteChatSession } from '../../api/api';
import { abortAll } from '../../redux/slices/requestSlice';
import { fetchSpaces } from '../../redux/slices/spaceSlice';
import { handleApiError } from '../../helpers/handleApiError';
export default function Sidebar({
  createNewChat,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  isDarkMode,
  isSidebarOpen,
  setIsSidebarOpen,
  isMobile,
  allMessages,
  onUpgradeFlowComplete,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get('sessionId');
  const isDocumentAction = location.pathname.startsWith('/app/document/action');
  let currentTab;
  let currentChatId;
  if (isDocumentAction && sessionId) {
    // Only treat as chat WHEN a session exists
    currentTab = 'chat';
    currentChatId = sessionId;
  } else if (isDocumentAction && !sessionId) {
    // Document action page without session → NOT chat
    currentTab = 'document';
    currentChatId = null;
  } else {
    currentTab = location.pathname.split('/')[2];
    currentChatId = location.pathname.split('/')[3];
  }

  const { user } = useSelector((state) => state.auth);

  const { stats, loading } = useSelector((state) => state.userStatistics);

  const { sessions: chatHistory, loading: loadingChats } = useSelector(
    (state) => state.chatSessions,
  );
  const currentChat = chatHistory.find(
    (c) => c.chat_session_id === currentChatId,
  );

  const isNewInsight =
    currentTab === 'chat' &&
    !location.state?.isResearchSession &&
    (!currentChatId ||
      (!currentChat &&
        allMessages[currentChatId]?.length <= 1 &&
        !currentChat?.has_contract));
  useEffect(() => {
    const handleClickOutside = () => setMenuOpenId(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(fetchChatSessions());
  }, [dispatch]);

  const iconSize = isMobile ? 12 : 18;
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const [renameId, setRenameId] = useState(null);
  const [originalRenameTitle, setOriginalRenameTitle] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteTitle, setDeleteTitle] = useState('');

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isSpacesOpen, setIsSpacesOpen] = useState(false);
  const [isResearchOpen, setIsResearchOpen] = useState(false);
  const handleLogout = () => {
    // ✅ mark this as manual logout
    sessionStorage.setItem('manualLogout', 'true');

    dispatch(logoutThunk());
    dispatch(clearReport());
    dispatch(clearDocument());
    dispatch(resetStreaming());

    navigate('/login', { replace: true }); // 👈 important
  };
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobile, isSidebarOpen]);

  useEffect(() => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    } else {
      setIsSidebarOpen(false);
    }
  }, [isMobile, setIsSidebarCollapsed, setIsSidebarOpen]);
  useEffect(() => {
    if (currentTab !== 'spaces') {
      setIsSpacesOpen(false);
    }
  }, [currentTab]);

  const analysisRouteMap = {
    risk: 'risk-analysis',
    full: 'full-contract-analysis',
    clauses: 'extract-key-clauses',
    compliance: 'compliance-check',
    qa: 'question--answer',
  };

  const planUsageProps = {
    chatbotUsed: loading
      ? 0
      : stats?.raw_features?.chatbot_queries?.value -
          stats?.usage_month_to_date?.chatbot_queries?.value || 0,

    chatbotTotal: loading
      ? 0
      : stats?.raw_features?.chatbot_queries?.value || 0,

    researchUsed: loading
      ? 0
      : stats?.raw_features?.professional_research?.value -
          stats?.usage_month_to_date?.professional_research?.value || 0,

    researchTotal: loading
      ? 0
      : stats?.raw_features?.professional_research?.value || 0,

    docUsed: loading
      ? 0
      : stats?.raw_features?.document_analysis?.value -
          stats?.usage_month_to_date?.document_analysis?.value || 0,

    docTotal: loading ? 0 : stats?.raw_features?.document_analysis?.value || 0,

    isDarkMode,
    onUpgradeFlowComplete,
  };

  const activeOrder = user?.subscription || null;

  return (
    <>
      <div
        className={`
    transition-all duration-300 flex flex-col z-50 overflow-x-hidden
${
  isMobile
    ? `fixed top-0 left-0 bottom-0
 w-64 z-50 shadow-2xl transform ${
   isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
 }`
    : `${isSidebarCollapsed ? 'w-11 md:w-16' : 'w-64'}`
}
${isDarkMode ? 'bg-[#1F2937] border-gray-800' : 'bg-white border-gray-100'}

    ${isDarkMode ? 'bg-[#1F2937] border-gray-800' : 'bg-white border-gray-100'}
    border-r drop-shadow-lg h-screen overflow-y-auto overscroll-contain
  `}
      >
        <div className="flex items-center p-4 border-b border-gray-200">
          {!(isSidebarCollapsed && !isSidebarOpen) && (
            <div className="flex items-center flex-grow">
              <div>
                <a href="/" className="block">
                  <img
                    src={isDarkMode ? LogoDark : Logo}
                    alt="Legal Intelligence Logo"
                    className="w-20 h-auto"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          )}

          {!isMobile && isSidebarCollapsed && (
            <img
              src={isDarkMode ? CollapsedLogoDark : CollapsedLogo}
              alt="Collapsed Logo"
              className="h-6 w-auto mx-auto mb-1"
            />
          )}
          {!isMobile && !isSidebarOpen && (
            <button
              onClick={(e) => {
                setIsSidebarCollapsed(!isSidebarCollapsed);
                e.currentTarget.blur();
              }}
              data-tooltip-id="sidebar-tooltip"
              data-tooltip-content={
                isSidebarCollapsed ? 'Lock Sidebar' : 'Hide Sidebar'
              }
              data-tooltip-place="right"
              className={`rounded-full hover:bg-brand hover:text-white transition-colors ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}
            >
              {isSidebarCollapsed ? (
                <ChevronRight size={iconSize} />
              ) : (
                <ChevronLeft size={iconSize} />
              )}
            </button>
          )}
        </div>

        {/* Navigation menu */}
        <div className="flex-1 overflow-y-auto hide-scrollbar">
          <nav className="flex-grow ">
            <ul className="space-y-0 px-2 mt-2">
              <li>
                <button
                  onClick={() => {
                    if (!isNewInsight) {
                      // 👈 only create if NOT already in a fresh session
                      createNewChat();
                    }
                    if (isMobile) setIsSidebarOpen(false);
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content={
                    isSidebarCollapsed ? 'New Legal Insights' : undefined
                  }
                  data-tooltip-place="right"
                  className={`w-full flex items-center justify-between p-2 rounded-sm transition-colors
    ${
      isNewInsight
        ? 'bg-brand text-white'
        : isDarkMode
          ? 'text-white hover:bg-brand hover:text-white'
          : 'text-black hover:bg-brand hover:text-white'
    }
  `}
                >
                  <div className="flex items-center">
                    <img
                      src={
                        isNewInsight || isHovered
                          ? ScaleLogo
                          : isDarkMode
                            ? ScaleLogo
                            : ScaleLogo2
                      }
                      alt="Scale Logo"
                      className="h-5 w-5"
                    />

                    {!isSidebarCollapsed && (
                      <span className="ml-3 text-sm">New Legal Insights</span>
                    )}
                  </div>
                </button>
              </li>

              <li className="upload-tour">
                <button
                  onClick={() => {
                    navigate('/app/document');
                    if (isMobile) setIsSidebarOpen(false);
                  }}
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content={
                    isSidebarCollapsed ? 'Document Check' : undefined
                  }
                  data-tooltip-place="right"
                  className={`w-full flex items-center justify-between p-2 rounded-sm transition-colors
      ${
        currentTab === 'document'
          ? 'bg-brand text-white'
          : isDarkMode
            ? 'text-white hover:bg-brand hover:text-white'
            : 'text-black hover:bg-brand hover:text-white'
      }`}
                >
                  <div className="flex items-center">
                    <FileText className="h-5 w-5" />
                    {!(isSidebarCollapsed && !isSidebarOpen) && (
                      <span
                        className={`ml-3 text-sm font-normal ${
                          isDarkMode && currentTab !== 'document'
                            ? 'text-white'
                            : ''
                        }`}
                      >
                        Document Check
                      </span>
                    )}
                  </div>
                </button>
              </li>

              <li>
                <button
                  onClick={() => {
                    if (currentTab === 'spaces') {
                      setIsSpacesOpen((prev) => !prev);
                    } else {
                      navigate('/app/spaces');
                      setIsSpacesOpen(true);
                      if (isMobile) setIsSidebarOpen(false);
                    }
                  }}
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content={
                    isSidebarCollapsed ? 'Spaces' : undefined
                  }
                  data-tooltip-place="right"
                  className={`w-full flex items-center justify-between p-2 rounded-sm transition-colors ${
                    currentTab === 'spaces'
                      ? 'bg-brand text-white'
                      : isDarkMode
                        ? 'text-white hover:bg-brand hover:text-white'
                        : 'text-black hover:bg-brand hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <FolderOpen
                      className={`h-5 w-5 ${
                        currentTab === 'spaces'
                          ? 'text-white'
                          : isDarkMode
                            ? 'text-white'
                            : ''
                      }`}
                    />
                    {!isSidebarCollapsed && (
                      <span className="ml-3 text-sm font-normal">Spaces</span>
                    )}
                  </div>
                  {/* 
                {!isSidebarCollapsed &&
                  (isSpacesOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  ))} */}
                </button>

                {/* {isSpacesOpen && !isSidebarCollapsed && (
                <ul className="ml-8 mt-1 space-y-1 transition-all duration-300 ease-in-out">
                  {['Space 1', 'Space 2'].map((space, idx) => (
                    <li key={idx}>
                      <button
                        onClick={() => {
                          // navigate(`/app/spaces/${idx + 1}`);
                          if (isMobile) setIsSidebarOpen(false);
                        }}
                        className="text-left text-xs text-black px-2 py-1 rounded transition"
                      >
                        {space}
                      </button>
                    </li>
                  ))}
                </ul>
              )} */}
              </li>
              <li>
                <button
                  onClick={() => {
                    if (currentTab === 'researches') {
                      setIsResearchOpen((prev) => !prev);
                    } else {
                      navigate('/app/researches');
                      setIsResearchOpen(true);
                      if (isMobile) setIsSidebarOpen(false);
                    }
                  }}
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content={
                    isSidebarCollapsed ? 'Research Reports' : undefined
                  }
                  data-tooltip-place="right"
                  className={`w-full flex items-center justify-between p-2 rounded-sm transition-colors ${
                    currentTab === 'researches'
                      ? 'bg-brand text-white'
                      : isDarkMode
                        ? 'text-white hover:bg-brand hover:text-white'
                        : 'text-black hover:bg-brand hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <SearchCheck
                      className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`}
                    />
                    {!isSidebarCollapsed && (
                      <span className="ml-3 text-sm font-normal">
                        Research Reports
                      </span>
                    )}
                  </div>
                  {/* 
                {!isSidebarCollapsed &&
                  (isSpacesOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  ))} */}
                </button>

                {/* Accordion content */}
                {/* {isSpacesOpen && !isSidebarCollapsed && (
                <ul className="ml-8 mt-1 space-y-1 transition-all duration-300 ease-in-out">
                  {['Space 1', 'Space 2'].map((space, idx) => (
                    <li key={idx}>
                      <button
                        onClick={() => {
                          // navigate(`/app/spaces/${idx + 1}`);
                          if (isMobile) setIsSidebarOpen(false);
                        }}
                        className="text-left text-xs text-black px-2 py-1 rounded transition"
                      >
                        {space}
                      </button>
                    </li>
                  ))}
                </ul>
              )} */}
              </li>
            </ul>
            {/* .filter((chat) => chat.message_count > 0) */}
            {!(isSidebarCollapsed && !isSidebarOpen) && (
              <div className="mt-4 px-2">
                <h3
                  className={`px-1 text-[10px] tracking-wide mb-1 ${
                    isDarkMode ? 'text-white' : 'text-black'
                  }`}
                >
                  Recent Queries
                </h3>

                <div className="h-[200px] overflow-y-auto super-ultra-thin">
                  {loadingChats ? (
                    <ul className="space-y-1.5 animate-pulse">
                      {Array.from({ length: 3 }).map((_, idx) => (
                        <li key={idx}>
                          <div
                            className={`w-full text-left p-2 rounded border text-xs ${
                              isDarkMode
                                ? 'bg-gray-800 border-gray-700'
                                : 'bg-white border-gray-100'
                            }`}
                          >
                            <div className="h-3 w-3/4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>

                            <div className="flex items-center justify-between text-[10px]">
                              <div className="flex items-center gap-1">
                                <div className="h-3 w-3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                <div className="h-2 w-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="h-3 w-3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                <div className="h-2 w-12 bg-gray-300 dark:bg-gray-600 rounded"></div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : chatHistory.length === 0 ? (
                    <p className="text-xs text-gray-500 px-2">
                      No recent queries
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {chatHistory.map((chat) => (
                        <li
                          key={chat.chat_session_id}
                          className="relative group"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData(
                              'chatId',
                              chat.chat_session_id,
                            );
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                        >
                          <button
                            onClick={() => {
                              if (chat.has_contract) {
                                const route =
                                  analysisRouteMap[chat.analysis_type] ||
                                  chat.analysis_type;

                                navigate(
                                  `/app/document/action/${route}?sessionId=${chat.chat_session_id}`,
                                );
                              } else {
                                navigate(`/app/chat/${chat.chat_session_id}`);
                              }

                              if (isMobile) setIsSidebarOpen(false);
                            }}
                            className={`w-full text-left p-2 rounded border-b text-xs transition-all duration-150
  ${
    currentChatId === chat.chat_session_id
      ? 'bg-brandLighter border- border-brandLight text-gray-900'
      : isDarkMode
        ? 'bg-gray-800 border-gray-700 text-white hover:bg-brandLighter hover:border-brandLight hover:text-gray-900'
        : 'bg-white border-gray-200 text-gray-900 hover:bg-brandLighter hover:border-brandLight'
  }
`}
                          >
                            <div className="font-medium text-sm truncate mb-1 max-w-[160px] overflow-hidden whitespace-nowrap">
                              {chat.title || 'Untitled Conversation'}
                            </div>

                            <div className="flex items-center justify-between text-[9px]">
                              <div className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {chat.has_contract
                                  ? 'Document Review'
                                  : chat.session_type === 'conversation'
                                    ? 'Legal Chatbot'
                                    : chat.session_type}
                              </div>

                              {chat.spaces && chat.spaces.length > 0 && (
                                <div className="flex items-center gap-1 text-[9px]">
                                  <FolderOpen className="h-3 w-3" />
                                  {chat.spaces.map((s) => s.name).join(', ')}
                                </div>
                              )}
                            </div>
                          </button>

                          <div className="absolute top-2 right-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenId(
                                  menuOpenId === chat.chat_session_id
                                    ? null
                                    : chat.chat_session_id,
                                );
                              }}
                              className={`absolute -top-1 right-2 p-1 rounded-full transition-all duration-150
      ${
        currentChatId === chat.chat_session_id ||
        menuOpenId === chat.chat_session_id
          ? 'bg-brandLighter text-gray-900'
          : isDarkMode
            ? 'text-gray-300 hover:bg-brandLighter hover:text-gray-900'
            : 'text-gray-600 hover:bg-brandLighter hover:text-gray-900'
      }
    `}
                              title="More options"
                            >
                              <MoreVertical className="w-5 h-5 pointer-events-none" />
                            </button>

                            <AnimatePresence>
                              {menuOpenId === chat?.chat_session_id && (
                                <motion.div
                                  key={`menu-${chat?.chat_session_id}`}
                                  initial={{ opacity: 0, y: -5, scale: 0.98 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{
                                    opacity: 0,
                                    y: -5,
                                    scale: 0.98,
                                    transition: { delay: 0.05 },
                                  }}
                                  transition={{
                                    duration: 0.2,
                                    ease: 'easeOut',
                                  }}
                                  className={`absolute right-0 -top-1 mt-1 w-32 shadow-md rounded-md py-1 z-[9999]
          ${
            isDarkMode
              ? 'bg-gray-800 border border-brandLight'
              : 'bg-white border border-brandLight'
          }
        `}
                                >
                                  <button
                                    onClick={() => {
                                      setRenameId(chat.chat_session_id);
                                      setRenameTitle(chat.title || '');
                                      setOriginalRenameTitle(chat.title || '');
                                      setIsRenameOpen(true);
                                      setMenuOpenId(null);
                                    }}
                                    className={`block w-full text-left px-3 py-1 text-sm rounded-t-md transition-all duration-150 font-medium
            ${
              isDarkMode
                ? 'text-gray-200 hover:bg-brandLighter hover:text-gray-900'
                : 'text-gray-800 hover:bg-brandLighter hover:text-gray-900'
            }
          `}
                                  >
                                    ✏️ Rename
                                  </button>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMenuOpenId(null);
                                      setDeleteId(chat.chat_session_id);
                                      setDeleteTitle(
                                        chat.title || 'Untitled Chat',
                                      );
                                      setIsDeleteOpen(true);
                                    }}
                                    className={`block w-full text-left px-3 py-1 text-sm rounded-b-md transition-all duration-150 font-medium
            ${
              isDarkMode
                ? 'text-gray-200 hover:bg-red-600 hover:text-white hover:font-semibold'
                : 'text-gray-800 hover:bg-red-600 hover:text-white hover:font-semibold'
            }
          `}
                                  >
                                    🗑️ Delete
                                  </button>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </nav>
        </div>

        {!(isSidebarCollapsed && !isSidebarOpen) && (
          <div className="px-3 my-4">
            <PlanUsageCard {...planUsageProps} />
          </div>
        )}
        {isSidebarCollapsed && !isMobile && (
          <div className="flex items-center">
            <div className="px-3 flex justify-center my-4">
              <button className="plan-usage-trigger p-2 rounded-full bg-gradient-to-r from-brandDark to-brand text-white shadow-md hover:opacity-90">
                <Star className="h-4 w-4 text-white" />
              </button>
            </div>
          </div>
        )}

        <div className="border-t border-gray-700/50 p-3">
          <div className="flex items-center">
            <div className="relative">
              <div
                className={`flex ${
                  isSidebarCollapsed
                    ? 'flex-col items-center space-y-2'
                    : 'items-center space-x-2'
                }`}
              >
                <div
                  className="bg-gradient-to-r from-brandDark to-brand p-2 rounded-full shadow-inner cursor-pointer shadow-white/10"
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content={
                    isSidebarCollapsed ? user?.name : undefined
                  }
                  data-tooltip-place="right"
                >
                  <User className="h-4 w-4 text-white" />
                </div>

                {isSidebarCollapsed && (
                  <>
                    <button
                      onClick={() => {
                        navigate('/app/settings');
                        if (isMobile) setIsSidebarOpen(false);
                      }}
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-content="Settings"
                      data-tooltip-place="right"
                      className={`p-1 rounded-full transition-colors ${
                        currentTab === 'settings'
                          ? 'bg-brandLighter text-white'
                          : 'text-gray-400 hover:text-white hover:bg-brand'
                      }`}
                    >
                      <Settings
                        className={`h-4 w-4 ${
                          currentTab === 'settings' ? 'text-brand' : ''
                        }`}
                      />
                    </button>

                    <div className="flex justify-center w-full mt-2">
                      <button
                        onClick={() => setShowLogoutConfirm(true)}
                        data-tooltip-id="sidebar-tooltip"
                        data-tooltip-content="Logout"
                        data-tooltip-place="right"
                        className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-brand transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {!(isSidebarCollapsed && !isSidebarOpen) && (
              <div className="ml-2.5 overflow-hidden">
                <p
                  className={`text-[12px] truncate ${
                    isDarkMode ? 'text-white' : 'text-black'
                  }`}
                >
                  {user?.name}
                </p>
                <p
                  className={`text-xs font-sm truncate ${
                    isDarkMode ? 'text-white' : 'text-black'
                  }`}
                >
                  {activeOrder?.package_name || 'Free'} Plan
                </p>
              </div>
            )}

            {!(isSidebarCollapsed && !isSidebarOpen) && (
              <div className="ml-auto flex space-x-1">
                <button
                  onClick={() => {
                    navigate('/app/settings');
                    if (isMobile) setIsSidebarOpen(false);
                  }}
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content="Settings"
                  data-tooltip-place="right"
                  className={`p-1 rounded-full transition-colors  ${
                    currentTab === 'settings'
                      ? 'bg-brandLighter text-white'
                      : 'text-gray-400 hover:text-white hover:bg-brand'
                  }`}
                >
                  <Settings
                    className={`h-4 w-4 ${
                      currentTab === 'settings' ? 'text-brand' : ''
                    }`}
                  />
                </button>

                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content="Logout"
                  data-tooltip-place="right"
                  className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-brand transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            key="logout-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`rounded-2xl shadow-2xl w-full max-w-sm sm:p-8 p-5 transition-all ${
                isDarkMode
                  ? 'bg-gray-900 text-gray-100 border border-gray-700'
                  : 'bg-white text-gray-900 border border-gray-200'
              }`}
            >
              <h2
                className={`text-lg sm:text-xl font-bold mb-3 tracking-tight text-center ${
                  isDarkMode ? 'text-brand' : 'text-brand'
                }`}
              >
                Confirm Logout
              </h2>

              <p
                className={`text-sm mb-6 text-center ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                Are you sure you want to sign out? Your current session will
                end.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className={`w-full py-2 px-4 rounded-lg border font-medium transition-colors ${
                    isDarkMode
                      ? 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    handleLogout();
                    setShowLogoutConfirm(false);
                  }}
                  className="w-full py-2 px-4 rounded-lg bg-gradient-to-r from-brandDark to-brand text-white font-semibold shadow-sm transition-all hover:opacity-90"
                >
                  Logout
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isRenameOpen && (
          <motion.div
            key="rename-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`rounded-2xl shadow-2xl w-full max-w-sm sm:p-8 p-5 transition-all ${
                isDarkMode
                  ? 'bg-gray-900 text-gray-100'
                  : 'bg-white text-gray-900'
              }`}
            >
              <h2
                className={`text-lg font-bold mb-2 text-center ${
                  isDarkMode ? 'text-brand' : 'text-brand'
                }`}
              >
                Rename Chat
              </h2>

              <input
                type="text"
                value={renameTitle}
                onChange={(e) => setRenameTitle(e.target.value)}
                className={`w-full rounded px-3 py-2 mb-4 focus:ring-2 focus:ring-brand focus:outline-none border ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Enter new title..."
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setIsRenameOpen(false)}
                  className={`flex-1 py-2 border rounded-lg transition-all ${
                    isDarkMode
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>

                <button
                  disabled={
                    isRenaming ||
                    renameTitle.trim() === '' ||
                    renameTitle.trim() === originalRenameTitle.trim()
                  }
                  onClick={async () => {
                    setIsRenaming(true);

                    const newTitle = renameTitle.trim();

                    try {
                      dispatch(
                        updateChatTitleLocally({ chatId: renameId, newTitle }),
                      );

                      dispatch(
                        renameChatSessionThunk({ chatId: renameId, newTitle }),
                      );
                      dispatch(fetchSpaces());

                      toast.success('Chat renamed successfully ✨');
                    } catch (e) {
                      toast.error(e.message || 'Failed to rename chat ❌');
                    } finally {
                      setIsRenaming(false);
                      setIsRenameOpen(false);
                    }
                  }}
                  className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                    isRenaming
                      ? 'bg-gray-500 text-white cursor-not-allowed'
                      : renameTitle.trim() === '' ||
                          renameTitle.trim() === originalRenameTitle.trim()
                        ? isDarkMode
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-brandDark to-brand text-white hover:opacity-90'
                  }`}
                >
                  {isRenaming ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isDeleteOpen && (
          <motion.div
            key="delete-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`rounded-2xl shadow-2xl w-full max-w-sm sm:p-8 p-5 transition-all
          ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}
        `}
            >
              <h2
                className={`text-lg font-bold mb-3 text-center ${
                  isDarkMode ? 'text-brand' : 'text-brand'
                }`}
              >
                Confirm Delete
              </h2>

              <p
                className={`text-sm text-center mb-6 break-words whitespace-normal leading-relaxed
    ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}
  `}
              >
                Are you sure you want to delete{' '}
                <span
                  className={`font-semibold
      ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}
    `}
                >
                  “{deleteTitle}”
                </span>
                ? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsDeleteOpen(false)}
                  className={`flex-1 py-2 border rounded-lg transition-all font-medium ${
                    isDarkMode
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setIsDeleting(true);
                    dispatch(abortAll());

                    try {
                      await deleteChatSession(deleteId, user?.id);
                      toast.success('Chat deleted successfully');

                      dispatch(fetchChatSessions());
                      dispatch(fetchSpaces());
                      if (currentChatId === deleteId) {
                        navigate('/app/chat');
                      }
                    } catch (error) {
                      handleApiError(error, 'Failed to delete chat session');
                    } finally {
                      setIsDeleting(false);
                      setIsDeleteOpen(false);
                    }
                  }}
                  disabled={isDeleting}
                  className={`flex-1 py-2 rounded-lg font-semibold shadow-sm transition-all flex items-center justify-center gap-2
    ${
      isDeleting
        ? 'bg-red-700 opacity-70 cursor-not-allowed text-white'
        : 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:opacity-90'
    }
  `}
                >
                  {isDeleting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isMobile && (
        <Tooltip
          id="sidebar-tooltip"
          positionStrategy="fixed"
          events={['hover']} // 🔥 only show on hover
          className="!z-[9999] !text-xs !rounded-md !px-3 !py-1.5 !bg-gray-900 !text-white dark:!bg-brand !shadow-lg"
        />
      )}

      <Tooltip
        anchorSelect=".plan-usage-trigger"
        place="right"
        clickable
        data-tooltip-offset="10"
        className="!z-[999999] !p-0 !bg-transparent !shadow-none border-none"
      >
        <div className="w-64">
          <PlanUsageCard {...planUsageProps} hideUpgradeButton={true} />
        </div>
      </Tooltip>
    </>
  );
}
